
import { useEffect, useState, useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import Cookies from 'js-cookie';
import * as helpers from "../utils/utils.js"
import { useClientId } from "../context/context.js";

import { PDFDocument } from 'pdf-lib';


const apiBaseUrl = process.env.REACT_APP_API_URL;

export function useBooking() {

    const clientId = useClientId();
    const queryClient = useQueryClient();

    const getAuthHeaders = () => {
        const token = Cookies.get(`${clientId}_token`);
        return token
            ? { Authorization: `Bearer ${token}` }
            : undefined;
    };

    const submitSignatureRequest = async ({ id, signatureData }) => {
        const { data } = await axios.put(`${apiBaseUrl}/clients/${clientId}/bookings/send-signature/${id}`, signatureData);
        return data;
    };
    
    const { mutateAsync: submitSignature } = useMutation({
    mutationFn: submitSignatureRequest,
    onSuccess: (data) => {
        console.log('Signature added:', data);
    },
    onError: (error) => console.error("Error adding signature:", error),
    });


    const createBooking = async ( bookingDetails ) => {
        try {

            const discountsApplied = {};
            if (bookingDetails.appliedDiscount.amount > 0) {
                discountsApplied[bookingDetails.appliedDiscount.code] = bookingDetails.appliedDiscount.amount;
            }

            const costs = {
                deposit: {
                    amount: bookingDetails.totals.deposit,
                    paid: bookingDetails.amountPaid,
                    ...(bookingDetails.paymentInfo && { payment: bookingDetails.paymentInfo }),
                },
                remaining: {
                    amount: bookingDetails.totals.total - bookingDetails.totals.deposit,
                    paid: 0
                }
            }

            const requestBody = {
                customerId: bookingDetails.customerId,
                status: "confirmed",
                createdAt: Date.now(),
                createdBy: bookingDetails.admin ? "admin" : "customer",
                costs: { ...costs },
                slots: [...bookingDetails.selectedSlots],
                discounts: { ...discountsApplied },
            }

            // Save or process the booking
            const bookingResponse = await helpers.post(
                `/clients/${clientId}/bookings`,
                requestBody,
            );

            // TODO: send admin email if booking issue happens after payment

            if (bookingResponse.status !== 201) {
                console.error("Error creating booking:", bookingResponse);
                return {success: false, data: bookingResponse}
            } 

            return {success: true, data: bookingResponse.data.booking}

        } catch (error) {
            console.error("Error creating booking:", error);
            return {success: false, data: error}
        }
    };

    const checkoutRequest = async ({
        customer,
        selectedSlots,
        slotIds,
        discountCode,
        discountCodes,
        payment,
        createdBy = "customer",
    }) => {
        const token = Cookies.get(`${clientId}_token`);
        const headers = token
            ? { Authorization: `Bearer ${token}` }
            : undefined;

        const { data } = await axios.post(
            `${apiBaseUrl}/clients/${clientId}/checkout`,
            {
                customer,
                slotIds: slotIds || selectedSlots.map((slot) => slot.id),
                discountCode,
                discountCodes,
                payment: payment || { type: "none" },
                createdBy,
            },
            { headers },
        );

        return data;
    };

    const checkoutReservation = useMutation({
        mutationFn: checkoutRequest,
        onSuccess: (data) => {
            console.log('Checkout completed:', data);
            queryClient.invalidateQueries({ queryKey: ['bookingData'] });
        },
        onError: (error) => console.error("Error completing checkout:", error),
    });

    const getUpcomingBookings = useCallback(async (limit, firstDay) => {
        try {

            if (!firstDay) {
                firstDay = new Date();
                firstDay.setDate(firstDay.getDate() - 1);
            }

            const bookingResponse = await helpers.get(
                `/clients/${clientId}/bookings?firstDay=${firstDay.valueOf()}&limit=${limit}`,
            );

            
            if (bookingResponse.status !== 200) {
                console.error("Error fetching bookings:", bookingResponse);
                return;
            } 


            const bookingData = bookingResponse.data;

            const bookingsWithCustomers = await Promise.all(
                bookingData.map(async (booking) => {
                    const customerResponse = await helpers.get(
                        `/clients/${clientId}/customers/${booking.customerId}`
                    );
                    return {
                        ...booking,
                        customer: customerResponse.data,
                    };
                })
            );

            bookingsWithCustomers.sort((a,b) => a.firstDay - b.firstDay)
    
            return bookingsWithCustomers;

        } catch (error) {
            console.error("Error fetching bookings:", error);
            return;
        }
    }, [clientId]);

    const getBookingById = async (id) => {
        const { data } = await axios.get(`${apiBaseUrl}/clients/${clientId}/bookings/${id}`);
        return data;
    }

    const getBookings = async () => {
        const { data } = await axios.get(`${apiBaseUrl}/clients/${clientId}/bookings/`);
        return data;
    }

    const getFullBooking = async (id) => {
        const { data } = await axios.get(`${apiBaseUrl}/clients/${clientId}/bookings/${id}/get-full`);
        return data;
    }


    const updateBookingRequest = async ({ id, bookingData }) => {
        const { data } = await axios.put(`${apiBaseUrl}/clients/${clientId}/bookings/${id}`, bookingData);
        return data;
    };
    
    const { mutateAsync: updateBooking } = useMutation({
    mutationFn: updateBookingRequest,
    onSuccess: (data) => {
        console.log('Booking updated:', data);
        queryClient.invalidateQueries({ queryKey: ['bookingData'] });
    },
    onError: (error) => console.error("Error updating booking:", error),
    });

    const chargeSavedCardRequest = async ({ id, costKeys }) => {
        const { data } = await axios.post(
            `${apiBaseUrl}/clients/${clientId}/bookings/${id}/charge-saved-card`,
            { costKeys },
            { headers: getAuthHeaders() },
        );

        return data;
    };

    const chargeSavedCard = useMutation({
        mutationFn: chargeSavedCardRequest,
        onSuccess: (data) => {
            console.log('Saved card charged:', data);
            queryClient.invalidateQueries({ queryKey: ['bookingData'] });
            queryClient.invalidateQueries({ queryKey: ['customerCards'] });
        },
        onError: (error) => console.error("Error charging saved card:", error),
    });

    const ensureSquareCustomerRequest = async (bookingId) => {
        const { data } = await axios.post(
            `${apiBaseUrl}/clients/${clientId}/bookings/${bookingId}/ensure-square-customer`,
            {},
            { headers: getAuthHeaders() },
        );

        return data;
    };

    const ensureSquareCustomer = useMutation({
        mutationFn: ensureSquareCustomerRequest,
        onSuccess: (data) => {
            console.log('Square customer ready:', data);
            queryClient.invalidateQueries({ queryKey: ['customerData'] });
        },
        onError: (error) => console.error("Error ensuring Square customer:", error),
    });

    const getChecklistTemplates = async ({ active } = {}) => {
        const params = new URLSearchParams();
        if (active !== undefined) params.append("active", String(active));

        const { data } = await axios.get(
            `${apiBaseUrl}/clients/${clientId}/checklist-templates${params.toString() ? `?${params.toString()}` : ""}`,
            { headers: getAuthHeaders() },
        );

        return data;
    };

    const getBookingChecklists = async (bookingId) => {
        const { data } = await axios.get(
            `${apiBaseUrl}/clients/${clientId}/bookings/${bookingId}/checklists`,
            { headers: getAuthHeaders() },
        );

        return data;
    };

    const createBookingChecklistRequest = async ({ id, checklistData }) => {
        const { data } = await axios.post(
            `${apiBaseUrl}/clients/${clientId}/bookings/${id}/checklists`,
            checklistData,
            { headers: getAuthHeaders() },
        );

        return data;
    };

    const createBookingChecklist = useMutation({
        mutationFn: createBookingChecklistRequest,
        onSuccess: (data, variables) => {
            console.log('Checklist saved:', data);
            queryClient.invalidateQueries(["bookingData", variables.id]);
            queryClient.invalidateQueries(["bookingChecklists", variables.id]);
        },
        onError: (error) => console.error("Error saving checklist:", error),
    });

    const updateBookingChecklistRequest = async ({ id, checklistId, checklistData }) => {
        const { data } = await axios.put(
            `${apiBaseUrl}/clients/${clientId}/bookings/${id}/checklists/${checklistId}`,
            checklistData,
            { headers: getAuthHeaders() },
        );

        return data;
    };

    const updateBookingChecklist = useMutation({
        mutationFn: updateBookingChecklistRequest,
        onSuccess: (data, variables) => {
            console.log('Checklist updated:', data);
            queryClient.invalidateQueries(["bookingData", variables.id]);
            queryClient.invalidateQueries(["bookingChecklists", variables.id]);
        },
        onError: (error) => console.error("Error updating checklist:", error),
    });

    const deleteBookingChecklistRequest = async ({ id, checklistId }) => {
        const { data } = await axios.delete(
            `${apiBaseUrl}/clients/${clientId}/bookings/${id}/checklists/${checklistId}`,
            { headers: getAuthHeaders() },
        );

        return data;
    };

    const deleteBookingChecklist = useMutation({
        mutationFn: deleteBookingChecklistRequest,
        onSuccess: (data, variables) => {
            console.log('Checklist deleted:', data);
            queryClient.invalidateQueries(["bookingData", variables.id]);
            queryClient.invalidateQueries(["bookingChecklists", variables.id]);
        },
        onError: (error) => console.error("Error deleting checklist:", error),
    });

    
    const emailContractRequest = async ( bookingId ) => {
        const { data } = await axios.post(`${apiBaseUrl}/clients/${clientId}/bookings/send-contract?bookingId=${bookingId}`);
        return data;
    };
    
    const emailContracts = useMutation({
    mutationFn: emailContractRequest,
    onSuccess: (data) => {
        console.log('Contract Emailed:', data);
    },
    onError: (error) => console.error("Error sending contract:", error),
    });
      
    const emailCheckinRequest = async ( bookingId ) => {
        const { data } = await axios.post(`${apiBaseUrl}/clients/${clientId}/bookings/send-checkin?bookingId=${bookingId}`);
        return data;
    };
    
    const emailCheckin = useMutation({
    mutationFn: emailCheckinRequest,
    onSuccess: (data) => {
        console.log('Checkin Emailed:', data);
        queryClient.invalidateQueries({ queryKey: ['bookingData'] });
    },
    onError: (error) => console.error("Error sending contract:", error),
    });

    const emailReviewRequestRequest = async ( customerId ) => {
        const { data } = await axios.post(`${apiBaseUrl}/clients/${clientId}/send-review-request?customerId=${customerId}`);
        return data;
    };
    
    const emailReviewRequest = useMutation({
    mutationFn: emailReviewRequestRequest,
    onSuccess: (data) => {
        console.log('Review Request Emailed:', data);
    },
    onError: (error) => console.error("Error sending review request:", error),
    });

    const emailLicenseRequirementRequest = async ( customerId ) => {
        const { data } = await axios.post(`${apiBaseUrl}/clients/${clientId}/send-license-requirement?customerId=${customerId}`);
        return data;
    };
    
    const emailLicenseRequirement = useMutation({
    mutationFn: emailLicenseRequirementRequest,
    onSuccess: (data) => {
        console.log('License Requirement Emailed:', data);
    },
    onError: (error) => console.error("Error sending license requirement:", error),
    });
      

    const emailConfirmationRequest = async (bookingId) => {
        const { data } = await axios.post(
            `${apiBaseUrl}/clients/${clientId}/send-confirmation?bookingId=${bookingId}`,
        );

        return data;
    };

    const getTotalMoney = async (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.year) params.append("year", filters.year);
        if (filters.month) params.append("month", filters.month);
        if (filters.day) params.append("day", filters.day);
        const query = params.toString();

        const { data } = await axios.get(
            `${apiBaseUrl}/clients/${clientId}/bookings/total-money${query ? `?${query}` : ""}`,
        );

        return data;
    };

    const emailConfirmation = useMutation({
        mutationFn: emailConfirmationRequest,
        onSuccess: (data) => {
            console.log('Email Confirmation Sent', data);
        },
        onError: (error) => console.error("Error sending email confirmation:", error),
    });


    const contractsUrl = (bookingId) => `${apiBaseUrl}/clients/${clientId}/bookings/generate-contract?bookingId=${bookingId}`;
    const confirmationUrl = (bookingId) => `${window.location.origin}/${clientId}/confirmation?booking_id=${bookingId}&confirmationOnly=true`;

    
      
    return {
        createBooking,
        checkoutReservation,
        emailConfirmation,
        emailContracts,
        emailCheckin,
        emailReviewRequest,
        emailLicenseRequirement,
        emailContractRequest,
        getUpcomingBookings,
        getBookingById,
        updateBooking,
        chargeSavedCard,
        ensureSquareCustomer,
        getChecklistTemplates,
        getBookingChecklists,
        createBookingChecklist,
        updateBookingChecklist,
        deleteBookingChecklist,
        submitSignature,
        getFullBooking,
        contractsUrl,
        confirmationUrl,
        getBookings,
        getTotalMoney,
    };
}
